// The token's menu (issue 812): the right button on a thing — or the Menu
// key, or Shift+F10, with one thing selected — opens a short menu at the
// pointer. It is a way INTO what exists, never a second copy of it: the
// Properties panel for everyone; for the DM the owners (the panel's own
// `object.assign`), reveal and hide through the board's one reveal gate,
// the lock, the turn order, and removal through the one delete
// confirmation. A player's menu holds only what they may do.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { DISPOSITIONS, MARKERS, combatantOf, dispositionOf, emptyEncounter, memberLabel, type DmObject, type Encounter } from '@dragons-table/vtt-shared';
import { dialogs } from '../dialog';
import { useShell } from '../shell/context';
import { useTableState } from '../table/TableState';
import { useMayAct } from '../table/mayAct';
import { addToEncounter, followCandidates } from '../shell/panels/PropertiesPanel';
import { SheetLink, sheetAction } from '../shell/panels/CharacterParts';
import { RolledRestItem } from '../shell/panels/RolledRestParts';
import { aim } from './target';
import { selection } from './selection';
import { boardBus, type TokenMenuAt } from './bus';
import { wikiCard } from './whatsThisTool';
import { wikiPicker } from './WikiOverlays';
import { markerLabel } from './tokenStatus';
import { useLook } from '../shell/useLook';

const item = 'flex w-full items-center gap-2 px-3 py-1 text-left text-sm hover:bg-stone-800 focus:bg-stone-800 focus:outline-none disabled:opacity-50';

/**
 * Into Properties' Follows picker (issue 1031): its section opened first where it is closed (issue 1575: the DM's
 * Advanced, an owner's Token), then the picker focused.
 */
function focusFollow() {
  const pick = document.querySelector<HTMLElement>('[data-testid="follow-picker"] select');
  const section = pick?.closest<HTMLElement>('[data-open]');
  if (section?.dataset.open === 'false') section.querySelector<HTMLElement>('[data-testid^="section-head-"]')?.click();
  setTimeout(() => pick?.focus(), 0);
}

export function TokenMenu() {
  const { store, role, act, viewingAs } = useTableState();
  const { table } = useShell();
  const mayAct = useMayAct();
  const [at, setAt] = useState<TokenMenuAt | null>(null);
  // Rename… (qa-runs#362): the name being typed, in place in the menu; null while not renaming.
  const [renaming, setRenaming] = useState<string | null>(null);
  const look = useLook();
  const ref = useRef<HTMLDivElement>(null);
  const before = useRef<HTMLElement | null>(null);

  useEffect(() => boardBus.onTokenMenu((next) => { before.current = document.activeElement as HTMLElement | null; setRenaming(null); setAt(next); }), []);
  const close = (restore = true) => { setAt(null); setRenaming(null); if (restore) before.current?.focus(); };
  // Where the focus goes back to when the window RETURNS, after the menu closed because the window lost focus. Handing
  // it back while the window is away does not hold in Chrome: on return the focus sat on the body, the arrows did
  // nothing and the Menu key opened the browser's own menu (QA retest of issue 844). This listener outlives the menu.
  const onReturn = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const back = () => {
      const el = onReturn.current;
      onReturn.current = null;
      if (el && el.isConnected && (document.activeElement === document.body || document.activeElement === null)) el.focus();
    };
    window.addEventListener('focus', back);
    return () => window.removeEventListener('focus', back);
  }, []);

  // Anything that is not the menu closes it: a press elsewhere, Escape, or the window losing focus.
  useEffect(() => {
    if (!at) return undefined;
    if (renaming === null) ref.current?.querySelector<HTMLElement>('[role^="menuitem"]:not([disabled])')?.focus();
    const away = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) close(false); };
    // Losing the window closes it as a native menu does, and the board gets the focus back — now, and again when the
    // window returns (QA run 2026-09-26-run-2, F1; the retest showed the first alone does not hold).
    const blur = () => { onReturn.current = before.current; close(true); };
    // Escape closes it wherever the focus happens to be.
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); close(); } };
    document.addEventListener('pointerdown', away, true);
    document.addEventListener('keydown', esc);
    window.addEventListener('blur', blur);
    return () => { document.removeEventListener('pointerdown', away, true); document.removeEventListener('keydown', esc); window.removeEventListener('blur', blur); };
  }, [at]); // eslint-disable-line react-hooks/exhaustive-deps

  // The DM's menu needs `tokens` (issues 1159, 1160): a co-DM without it acts as a player who owns nothing.
  const mayTokens = useMayAct()('object.hide');
  const o = at ? store.state.objects[at.id] : undefined;
  // The thing left the board while the menu was open (another person's change): nothing to act on.
  useEffect(() => { if (at && !o) setAt(null); }, [at, o]);
  // The menu's real height, once drawn: the estimate below counts mouse-sized rows, and a finger's are 44 px (issue 1179),
  // so on a tablet the estimate put the menu's foot below the screen.
  const [drawnH, setDrawnH] = useState<number | null>(null);
  useLayoutEffect(() => {
    const h = at ? ref.current?.offsetHeight || null : null;
    if (h !== drawnH) setDrawnH(h);
  });
  if (!at || !o) return null;

  const isDm = role === 'dm' && !viewingAs && mayTokens;
  const dm = o as DmObject;
  const players = table.members.filter((m) => m.role === 'player');
  const enc = (store.state.encounter ?? emptyEncounter()) as Encounter;
  const inFight = isDm && o.kind === 'token' ? combatantOf(enc, o.id) : null;
  const run = (fn: () => void) => () => { close(); fn(); };
  const saveName = () => {
    const next = (renaming ?? '').trim();
    if (next && next !== o.name) act({ kind: 'object.rename', id: o.id, name: next, rev: dm.rev });
    close();
  };
  // The site's page for the sheet (issues 848, 1053): "View sheet" for the DM, "Open my sheet" for an owner.
  const sheet = sheetAction(o, isDm);
  // Dead or not (issue 855): the DM's for any token, an owner's for their own.
  const mayMarkDead = o.kind === 'token' && (isDm || (!viewingAs && 'mine' in o && o.mine));
  const deadItem = mayMarkDead && (
    <button type="button" role="menuitem" className={item} onClick={run(() => act({ kind: 'object.dead', id: o.id, dead: !o.dead }))}>{o.dead ? 'Not dead' : 'Mark dead'}</button>
  );

  const onKey = (e: React.KeyboardEvent) => {
    if (renaming !== null) return; // the name field's own keys
    const items = Array.from(ref.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]:not([disabled])') ?? []);
    const i = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); items[(i + 1) % items.length]?.focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); items[(i - 1 + items.length) % items.length]?.focus(); }
    else if (e.key === 'Home') { e.preventDefault(); items[0]?.focus(); }
    else if (e.key === 'End') { e.preventDefault(); items[items.length - 1]?.focus(); }
    else if (e.key === 'Tab') { e.preventDefault(); close(); }
  };

  // Kept on screen: a menu opened near the right or bottom edge opens back towards the middle.
  // Turning (issue 1030): whoever may mark it dead may turn it — the DM any token, an owner their own. 45° a click.
  const turnBy = (d: number) => run(() => act({ kind: 'object.rotate', id: o.id, rotation: ((((('rotation' in o ? o.rotation : 0) ?? 0) + d) % 360) + 360) % 360 }));
  const rotateItems = mayMarkDead && (
    <>
      <button type="button" role="menuitem" className={item} onClick={turnBy(-45)}>Rotate left 45°</button>
      <button type="button" role="menuitem" className={item} onClick={turnBy(45)}>Rotate right 45°</button>
    </>
  );
  // Follow… (issue 1031): into Properties' picker, where there is something this token may follow — the DM, or its owner.
  const mayFollow = (isDm || mayMarkDead) && ('follows' in o && o.follows || followCandidates(o, store.state.objects as never, isDm).length > 0);
  const followItem = mayFollow && (
    <button type="button" role="menuitem" className={item} onClick={run(() => { boardBus.showTool('properties'); setTimeout(focusFollow, 0); })}>Follow…</button>
  );
  const hasWiki = o.kind === 'token' && 'wiki' in o && Boolean(o.wiki);
  const W = 232; const guessH = (isDm ? 370 + players.length * 30 : 74) + (hasWiki ? 30 : 0) + (sheet ? 30 : 0) + (mayMarkDead ? 60 : 0) + (!isDm && mayMarkDead ? 30 : 0) + (mayFollow ? 30 : 0);
  const H = drawnH ?? guessH;
  const left = Math.max(4, Math.min(at.x, window.innerWidth - W - 4));
  const top = Math.max(4, Math.min(at.y, window.innerHeight - H - 4));

  return (
    <div ref={ref} role="menu" aria-label={`${o.name ?? 'Token'}: actions`} data-testid="token-menu" onKeyDown={onKey} onContextMenu={(e) => e.preventDefault()}
      className="fixed z-50 min-w-[12rem] overflow-y-auto vtt-popover py-1 shadow-xl" style={{ left, top, width: W, maxHeight: window.innerHeight - 8 }}>
      <p className="vtt-title truncate px-3 pb-1 text-xs" aria-hidden="true">{o.name}</p>
      <button type="button" role="menuitem" className={item} onClick={run(() => boardBus.showTool('properties'))}>Properties</button>
      {/* The target this person's actions fire at (issue 1127): theirs alone, never sent. */}
      {/* Targeting leaves this person's own selection as it was (issue 1423): the press that opened the menu selected the target. */}
      {o.kind === 'token' && <button type="button" role="menuitem" className={item} onClick={run(() => { aim.set(aim.get() === o.id ? null : o.id); if (at.selected) selection.replace(at.selected); })}>{aim.get() === o.id ? 'Stop targeting' : 'Target'}</button>}
      <SheetLink o={o} isDm={isDm} menu className={item} onOpen={() => close(false)} />
      {/* Its campaign wiki page (issue 1155): the card, for anyone it says has one; the DM links one. */}
      {o.kind === 'token' && 'wiki' in o && o.wiki && <button type="button" role="menuitem" className={item} onClick={run(() => wikiCard.open({ id: o.id, x: at.x, y: at.y }))}>Wiki</button>}
      {!isDm && deadItem}
      {!isDm && rotateItems}
      {!isDm && followItem}
      {!isDm && mayMarkDead && o.kind === 'token' && <RolledRestItem id={o.id} at={at} onPick={() => close()} />}
      {isDm && (
        <>
          <div role="separator" className="my-1 border-t border-stone-700" />
          <div role="group" aria-label="Belongs to">
            <p className="px-3 text-xs text-stone-400">Belongs to the DM{players.length ? ', and to' : ''}</p>
            {players.map((m) => {
              const owns = dm.owners.includes(m.id);
              return (
                <button key={m.id} type="button" role="menuitemcheckbox" aria-checked={owns} className={item}
                  onClick={run(() => act({ kind: 'object.assign', id: o.id, owners: owns ? dm.owners.filter((x) => x !== m.id) : [...dm.owners, m.id] }))}>
                  <span aria-hidden="true" className="w-4">{owns ? '✓' : ''}</span>{memberLabel(m, store.state.objects)}
                </button>
              );
            })}
            {players.length === 0 && <p className="px-3 py-1 text-xs text-stone-500">Nobody else is seated at this table.</p>}
          </div>
          <div role="separator" className="my-1 border-t border-stone-700" />
          {/* Rename… (qa-runs#362): in place; Enter saves, Escape goes back to the menu. The one rename, `object.rename`. */}
          {renaming === null
            ? <button type="button" role="menuitem" className={item} onClick={() => setRenaming(o.name ?? '')}>Rename…</button>
            : (
              <form className="px-3 py-1" onSubmit={(e) => { e.preventDefault(); saveName(); }}>
                <input aria-label={`New name for ${o.name ?? 'the token'}`} data-testid="token-menu-rename" autoFocus maxLength={120} value={renaming}
                  className="w-full rounded bg-stone-800 px-1 py-0.5 text-sm" onChange={(e) => setRenaming(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); setRenaming(null); setTimeout(() => ref.current?.querySelector<HTMLElement>('[role^="menuitem"]')?.focus(), 0); }
                  }} />
              </form>
            )}
          {/* NPC dialog (qa-runs#1167): Say… opens the Dialogue panel with this token speaking. */}
          {mayAct('dialog.say') && <button type="button" role="menuitem" className={item} data-testid="token-menu-say" onClick={run(() => { dialogs.pickSpeaker(o.id); boardBus.showTool('dialogue'); })}>Say…</button>}
          <button type="button" role="menuitem" className={item} onClick={run(() => boardBus.requestReveal([o.id], dm.hidden))}>{dm.hidden ? 'Reveal' : 'Hide'}</button>
          <button type="button" role="menuitem" className={item} onClick={run(() => act({ kind: 'object.lock', id: o.id, locked: !o.locked }))}>{o.locked ? 'Unlock' : 'Lock'}</button>
          {deadItem}
          {rotateItems}
          {followItem}
          {o.kind === 'token' && <RolledRestItem id={o.id} at={at} onPick={() => close()} />}
          {o.kind === 'token' && (
            // Friend, neutral or foe (issue 1046; qa-runs#1157): the DM's; a player's token starts a friend, any other a foe.
            <div role="group" aria-label="Side" className="flex gap-1 px-3 py-1">
              {DISPOSITIONS.map((d) => (
                <button key={d} type="button" role="menuitemradio" aria-checked={dispositionOf(dm) === d}
                  className={`flex-1 rounded border px-1 py-0.5 text-xs ${dispositionOf(dm) === d ? 'border-amber-500 text-amber-200' : 'border-stone-700 hover:bg-stone-800'}`}
                  onClick={run(() => act({ kind: 'object.disposition', id: o.id, disposition: d }))}>{d[0]!.toUpperCase() + d.slice(1)}</button>
              ))}
            </div>
          )}
          {o.kind === 'token' && (
            // The marker colour (qa-runs#362), the same one Properties sets: offered here as well.
            <div role="group" aria-label="Colour" className="flex flex-wrap items-center gap-1 px-3 py-1" data-testid="token-menu-colour">
              {MARKERS.map((mk) => (
                <button key={mk} type="button" role="menuitemradio" aria-checked={dm.marker === mk} aria-label={markerLabel(mk)} title={markerLabel(mk)}
                  className={`h-5 w-5 rounded-full border-2 ${dm.marker === mk ? 'border-stone-100' : 'border-stone-700'}`} style={{ backgroundColor: look.marker(mk) }}
                  onClick={run(() => act({ kind: 'object.marker', id: o.id, marker: mk }))} />
              ))}
              <button type="button" role="menuitemradio" aria-checked={dm.marker === null} aria-label="No marker" className="rounded border border-stone-700 px-1 text-xs hover:bg-stone-800"
                onClick={run(() => act({ kind: 'object.marker', id: o.id, marker: null }))}>none</button>
            </div>
          )}
          {o.kind === 'token' && (
            <button type="button" role="menuitem" className={item} disabled={Boolean(inFight)} title={inFight ? `Acts as ${inFight.name} in the turn order.` : undefined}
              onClick={run(() => addToEncounter(act, [dm]))}>{inFight ? 'In the encounter' : 'Add to encounter'}</button>
          )}
          {/* The campaign wiki page it stands for (issue 1155): the DM's picker. */}
          {o.kind === 'token' && <button type="button" role="menuitem" className={item} onClick={run(() => wikiPicker.open(o.id))}>Wiki page…</button>}
          <div role="separator" className="my-1 border-t border-stone-700" />
          <button type="button" role="menuitem" className={`${item} text-red-300`} onClick={run(() => boardBus.requestDelete([o.id]))}>Remove…</button>
        </>
      )}
    </div>
  );
}
